import css from "./SvgCanvas.module.css";
import React, { useMemo, useState, useRef, useEffect } from "react";

import SvgCanvasCustomCursor from "./SvgCanvasCustomCursor/SvgCanvasCustomCursor";
import { canvasActions } from "../../store/canvasSlice";
import { ERASE_CURSOR_RADIUS } from "./SvgCanvasCustomCursor/EraseCursor";

import { useCanvas } from "../../hooks/useCanvas";
import { useDispatch } from "react-redux";
import { useDelaunayWorker } from "../../hooks/useDelaunayWorker";
import { MODES, type ModeKey } from "../../config/modes";
import { useEditorPosition } from "../../hooks/useEditorPosition";
import { useCanvasTransform } from "../../hooks/useCanvasTransform";
import type { Point } from "./renderers";

export type OnClickFn = React.SVGProps<SVGSVGElement>["onClick"];
export type OnDragFn = React.SVGProps<SVGSVGElement>["onPointerMove"];

// Helper function to create straight line from last point to current point (like Photoshop)
const constrainToStraightLine = (
	currentPoint: Point,
	lastPoint: Point,
): Point => {
	// For Photoshop-style straight lines, just return the current point as-is
	// The visual line will be drawn from lastPoint to currentPoint
	return [currentPoint[0], currentPoint[1]];
};

const SvgCanvas = () => {
	const { svgRef, isLoading } = useDelaunayWorker();
	const { editorPosition } = useEditorPosition();
	const [hoverPoint, setHoverPoint] = useState<{
		x: number;
		y: number;
	} | null>(null);
	const [isShiftHeld, setIsShiftHeld] = useState(false);
	const lastDrawnPointRef = useRef<Point | null>(null);
	const {
		transformStyle,
		isPanning,
		handleWheel,
		handleMouseDown,
		handleMouseMove: handleTransformMouseMove,
		handleMouseUp,
		transformToCanvasCoords,
	} = useCanvasTransform();

	const dispatch = useDispatch();
	const { mode, points } = useCanvas();
	const currentMode = useMemo(() => MODES.find((m) => m.key === mode), [mode]);

	// Update last drawn point when points change
	useEffect(() => {
		if (points.length > 0) {
			lastDrawnPointRef.current = points[points.length - 1];
		} else {
			lastDrawnPointRef.current = null;
		}
	}, [points]);

	// Track shift key state
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Shift") {
				setIsShiftHeld(true);
			}
		};

		const handleKeyUp = (e: KeyboardEvent) => {
			if (e.key === "Shift") {
				setIsShiftHeld(false);
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		document.addEventListener("keyup", handleKeyUp);

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.removeEventListener("keyup", handleKeyUp);
		};
	}, []);

	// Handler for both click and drag events
	const handleCanvasEvent = (
		e: React.MouseEvent<SVGSVGElement, MouseEvent>,
	) => {
		// Don't handle canvas events while panning
		if (isPanning) return;

		// Get the coordinates of the click relative to the SVG element
		const rect = e.currentTarget.getBoundingClientRect();
		const canvasCoords = transformToCanvasCoords(e.clientX, e.clientY, rect);
		let x = canvasCoords.x;
		let y = canvasCoords.y;

		// Apply shift key constraint for straight lines in draw mode
		if (mode === "draw" && e.shiftKey && lastDrawnPointRef.current) {
			const constrainedPoint = constrainToStraightLine(
				[x, y],
				lastDrawnPointRef.current,
			);
			x = constrainedPoint[0];
			y = constrainedPoint[1];
		}

		// Edit mode is momentary. It removes the polygon that was clicked using CSS,
		// but does not modify the state.
		// Therefore, recomputing the Delaunay triangulation will reset the canvas to pre-edit state.
		if (mode === "eraseFaces") {
			// check if clicked element is a polygon
			const target = e.target as SVGElement;

			if (target.tagName === "polygon") {
				target.style.display = "none";
			}

			return;
		}

		// Actions for each mode
		const actions = {
			draw: canvasActions.addPoint([x, y]),
			select: null, // Handle selection separately
			eraseVertices: canvasActions.erasePoints({
				x,
				y,
				radius: ERASE_CURSOR_RADIUS,
			}),
			eraseFaces: null,
		} as const satisfies Record<ModeKey, unknown>;

		// Track the last drawn point for shift key constraint
		if (mode === "draw") {
			lastDrawnPointRef.current = [x, y];
		}

		// Handle point selection separately
		if (mode === "select") {
			const target = e.target as SVGElement;
			if (target.tagName === "circle" && target.dataset.pointIndex) {
				const pointIndex = parseInt(target.dataset.pointIndex, 10);
				if (e.ctrlKey || e.metaKey) {
					dispatch(canvasActions.togglePointSelection(pointIndex));
				} else {
					dispatch(canvasActions.clearSelection());
					dispatch(canvasActions.selectPoint(pointIndex));
				}
			} else if (!e.ctrlKey && !e.metaKey) {
				dispatch(canvasActions.clearSelection());
			}
			return;
		}

		dispatch(actions[mode] ?? { type: "NOOP" });
	};

	const handleMouseMove = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
		handleTransformMouseMove(e);

		if (!isPanning) {
			const rect = e.currentTarget.getBoundingClientRect();
			const canvasCoords = transformToCanvasCoords(
				e.clientX,
				e.clientY,
				rect,
			);

			if (mode === "draw") {
				setHoverPoint(canvasCoords);
			} else {
				setHoverPoint(null);
			}

			// Handle dragging when mouse button is pressed (but not panning)
			if (e.buttons === 1 && !isPanning) {
				handleCanvasEvent(e);
			}
		}
	};

	const handleMouseLeave = () => {
		setHoverPoint(null);
	};

	return (
		<>
			{isLoading && (
				<div
					data-editor-position={editorPosition}
					className={css.loadingSpinner}
				/>
			)}
			<svg
				// * NOTE: SVG content is rendered *outside of React* for performance reasons.
				ref={svgRef}
				aria-label='Canvas'
				className={css.root}
				onClick={handleCanvasEvent}
				onPointerMove={handleMouseMove}
				onMouseLeave={handleMouseLeave}
				onWheel={handleWheel}
				onMouseDown={handleMouseDown}
				onMouseUp={handleMouseUp}
				style={{
					cursor: isPanning
						? "grabbing"
						: mode === "draw"
							? "crosshair"
							: "default",
				}}
			>
				<g style={transformStyle}>
					{/* This g element will be transformed, containing all the triangulation */}
				</g>
				{hoverPoint && mode === "draw" && (
					<>
						{/* Show line preview when shift is held and there's a last point */}
						{isShiftHeld && lastDrawnPointRef.current && (
							<line
								x1={lastDrawnPointRef.current[0]}
								y1={lastDrawnPointRef.current[1]}
								x2={hoverPoint.x}
								y2={hoverPoint.y}
								stroke='rgba(0, 0, 0, 0.5)'
								strokeWidth='2'
								strokeDasharray='4 4'
								pointerEvents='none'
							/>
						)}
						<circle
							cx={hoverPoint.x}
							cy={hoverPoint.y}
							r='3'
							fill='rgba(255, 255, 255, 0.8)'
							stroke='rgba(0, 0, 0, 0.5)'
							strokeWidth='1'
							pointerEvents='none'
						/>
					</>
				)}
			</svg>
			{currentMode?.key === "eraseVertices" && (
				<SvgCanvasCustomCursor mode={currentMode} svgRef={svgRef} />
			)}
		</>
	);
};

export default SvgCanvas;
